import { CSSProperties, MutableRefObject, useEffect, useMemo, useState } from 'react';

import type { BaseRowData } from '@adv-frontend/data-provider';
import { useResizeObserver } from '@direct-frontend/stdlib';

import { useGridResizeHandler } from '../../../hooks/useGridResizeHandler';
import type { GridHeader, GridTable } from '../../../types';

import styles from '../Grid.module.css';

/**
 * Пропсы компонента `Resizer`.
 *
 * @typeParam RowData Данные грида.
 */
type ResizerProps<RowData extends BaseRowData> = {
    /**
     * Инстанс грида.
     */
    grid: GridTable<RowData>;
    /**
     * Ссылка на scrollable элемент грида.
     */
    gridRef?: MutableRefObject<HTMLDivElement | null>;
    /**
     * Ссылка на глобальный ресайзер грида.
     */
    commonResizerRef?: MutableRefObject<HTMLDivElement | null>;
    /**
     * Инстанс шапки колонки.
     */
    header: GridHeader<RowData>;
    /**
     * Смещение слева для группы колонок.
     */
    groupLeftOffset?: number;
    /**
     * Смещение сверху для строки, в которой находится заголовок.
     */
    rowTopOffset?: number;
    /**
     * Смещение ползунка изменения размера относительно правой границы колонки.
     */
    resizerOffset: number | null;
    /**
     * Признак видимости.
     */
    isVisible: boolean;
};

/**
 * Компонент реcайзера для колонок грида.
 *
 * @typeParam RowData Данные грида.
 */
export function Resizer<RowData extends BaseRowData>({
    commonResizerRef,
    header,
    grid,
    gridRef,
    groupLeftOffset,
    resizerOffset,
    rowTopOffset = 0,
    isVisible,
}: ResizerProps<RowData>) {
    // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
    const [resizerHeight, setResizerHeight] = useState(gridRef?.current?.clientHeight ?? 0);

    const inlineStyles: CSSProperties = useMemo(
        () => ({
            height: `${resizerHeight - rowTopOffset}px`,
            visibility: isVisible ? 'visible' : 'hidden',
        }),
        [resizerHeight, rowTopOffset, isVisible]
    );

    // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
    const onResize = useGridResizeHandler(grid, header, gridRef?.current?.clientWidth);

    /**
     * При изменении ширины колонки передвигаем глобальный ресайзер для корректного отображения поверх грида,
     * ресайзер в ячейке необходим только для отображения при наведении на него.
     */
    useEffect(() => {
        let transformOffset = resizerOffset;

        if (transformOffset !== null) {
            const columnWidth = header.getSize();
            const resizerAbsoluteOffset = header.getStart() + columnWidth;
            const resizerAbsoluteOffsetWithScroll = gridRef?.current?.scrollLeft
                ? resizerAbsoluteOffset - gridRef.current.scrollLeft
                : resizerAbsoluteOffset;

            /** Для корректного отображения глобального ресайзера учитываем отступ для группы,
             *  в которой находится данная ячейка (необходимо для центральной и правой запиненных групп).
             */
            if (groupLeftOffset !== undefined) {
                transformOffset += groupLeftOffset;

                /** Для ресайзера в ячейки из правой запиненной области не учитываем горизонтальный скролл,
                 * для ячейки из центральной - учитываем.
                 */
                if (header.column.getIsPinned() === 'right') {
                    transformOffset += resizerAbsoluteOffset;
                } else {
                    transformOffset += resizerAbsoluteOffsetWithScroll;
                }
            } else {
                transformOffset += resizerAbsoluteOffset;
            }
        }

        if (commonResizerRef?.current) {
            commonResizerRef.current.style.transform = transformOffset ? `translateX(${transformOffset}px)` : '';
            commonResizerRef.current.style.visibility = transformOffset !== null ? 'visible' : 'hidden';
            commonResizerRef.current.style.height = `${resizerHeight - rowTopOffset}px`;
            commonResizerRef.current.style.top = `${rowTopOffset}px`;
        }
    }, [resizerHeight, groupLeftOffset, header, resizerOffset, commonResizerRef, gridRef, rowTopOffset]);

    const resizeObserver = useResizeObserver(() => {
        setResizerHeight(gridRef?.current?.clientHeight || 0);
    });

    useEffect(() => {
        if (gridRef?.current) {
            resizeObserver?.observe(gridRef.current);
        }

        return () => resizeObserver?.disconnect();
    }, [gridRef, resizeObserver]);

    return <div className={styles.cellResizer} data-testid="CellResizer" onMouseDown={onResize} style={inlineStyles} />;
}
